<script setup lang="ts">
    import type { Schemas, operations } from '#shopware';

    const props = defineProps<{
        orderId: string;
    }>();

    defineOptions({ name: 'AccountOrderDetails' });

    const { getErrorsCodes } = useCartNotification();
    const { hfpPushSuccess, hfpPushError } = useNotificationHelpers();
    const { t } = useI18n();

    const uri = ref('');

    onMounted(() => {
        uri.value = encodeURIComponent(window.location.href);
    });

    const { loadOrderDetails, order, shippingAddress, billingAddress } = useOrderDetails(props.orderId);

    const { addProducts, count } = useCart();
    const addingProducts = ref(false);

    onMounted(() => {
        loadOrderDetails();
    });

    const lineItems = computed<Array<Schemas['OrderLineItem']>>(() => order.value?.lineItems || []);

    const handleReorder = async () => {
        if (!order.value?.lineItems) {
            return;
        }
        const items = order.value?.lineItems?.reduce(
            (acc, lineItem) => {
                if (lineItem.type !== 'product' || lineItem.good === false) {
                    return acc;
                }

                acc.push({
                    id: lineItem.productId || lineItem.identifier,
                    quantity: lineItem.quantity,
                    type: 'product'
                });

                return acc;
            },
            [] as operations['addLineItem post /checkout/cart/line-item']['body']['items']
        );

        try {
            addingProducts.value = true;
            const itemsBefore = count.value;
            await addProducts(items);

            getErrorsCodes()?.forEach((element) => {
                hfpPushError(t(`errors.${element.messageKey}`, { ...element }));
            });

            if (itemsBefore < count.value) {
                hfpPushSuccess(t('account.messages.productsAdded'));
            }
        } catch (error) {
            console.error(error);
            hfpPushError(t('messages.error'));
        } finally {
            addingProducts.value = false;
        }
    };
</script>

<template>
    <div
        v-if="lineItems.length"
        class="order-details"
    >
        <div class="info-table">
            <div>{{ t('account.order.product') }}</div>
            <div>{{ t('account.order.quantity') }}</div>
            <div>{{ t('account.order.price') }}</div>
            <div>{{ t('account.order.subtotal') }}</div>
        </div>

        <AccountOrderLineItem
            v-for="lineItem in lineItems"
            :key="lineItem.identifier"
            :line-item="lineItem"
        />

        <div
            v-if="!uri.includes('loading')"
            class="address-container"
        >
            <div
                v-if="shippingAddress"
                class="overview-element"
            >
                <div class="overview-label">
                    {{ t('checkout.shippingAddressLabel') }}
                </div>
                <div class="overview-text">
                    <div>
                        {{ shippingAddress?.firstName }}
                        {{ shippingAddress?.lastName }}
                    </div>
                    <div v-if="shippingAddress?.additionalAddressLine1">
                        {{ shippingAddress?.additionalAddressLine1 }}
                    </div>
                    <div>
                        {{ shippingAddress?.street }}
                    </div>
                    <div>{{ shippingAddress?.city }}, {{ shippingAddress?.zipcode }}</div>
                </div>
            </div>
            <div
                v-if="billingAddress"
                class="overview-element"
            >
                <div class="overview-label">
                    {{ t('checkout.billingAddressLabel') }}
                </div>
                <div class="overview-text">
                    <div>{{ billingAddress.firstName }} {{ billingAddress.lastName }}</div>
                    <div v-if="billingAddress.additionalAddressLine1">
                        {{ billingAddress.additionalAddressLine1 }}
                    </div>
                    <div>
                        {{ billingAddress.street }}
                    </div>
                    <div>{{ billingAddress.city }}, {{ billingAddress.zipcode }}</div>
                </div>
            </div>
        </div>
        <div class="details-footer">
            <template v-if="order?.deliveries?.[0]?.trackingCodes && order?.deliveries[0].trackingCodes.length > 0">
                <a
                    class="tracking-link"
                    :href="order.deliveries[0].shippingMethod!.trackingUrl! + order?.deliveries[0].trackingCodes"
                >
                    {{ t('checkout.processing.track') }}
                </a>
            </template>

            <PrimaryButton
                v-if="!uri.includes('loading')"
                :button-text="t('checkout.processing.again')"
                product-button
                :disabled="addingProducts"
                @click="handleReorder"
            />
        </div>
    </div>
</template>

<style>
    @import '~/assets/scss/account/account-order-detail.scss';
</style>
